import React, { useState } from 'react';
import { supabase } from '@/lib/safeSupabase';
import { useAuth } from '@/hooks/useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Plus, Copy, Trash2, Loader2, UserMinus, ChevronRight, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { sanitizeError } from '@/lib/errorMessages';
import { createOrgLogger } from '@/lib/audit';
import { formatDistanceToNow } from 'date-fns';
import { INVITE_TTL_DAYS, inviteStatus, partitionInvites } from '@/components/org/inviteStatus';
import { fetchMemberStrategyFlags, setNodeFlag, clearNodeFlag } from '@/components/org/hierarchyLive';
import type { FlagKey } from '@/components/org/hierarchyTree';

const ROLE_LABELS: Record<string, string> = {
  org_owner:   'Owner',
  org_admin:   'Admin',
  agent:       'Agent',
  super_admin: 'Super Admin',
};

function RoleBadge({ role }: { role: string }) {
  const variant = { org_owner: 'default', org_admin: 'secondary', agent: 'outline', super_admin: 'default' }[role] as any ?? 'outline';
  return <Badge variant={variant} className="text-xs">{ROLE_LABELS[role] ?? role}</Badge>;
}

export default function TeamManagement() {
  const { user, org, isOrgOwner, isOrgAdmin, isSuperAdmin, isPlatformAdmin } = useAuth();
  const queryClient = useQueryClient();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('agent');
  const [generatedLink, setGeneratedLink] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [inviteView, setInviteView] = useState<'open' | 'accepted'>('open');

  const logAudit = createOrgLogger(org?.id);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['org-members', org?.id],
    queryFn: async () => {
      if (!org) return [];
      // Platform roles (super_admin / platform_admin) are org-LESS: their
      // user_roles rows carry org_id = NULL, so the org-scoped query below can
      // never see them. They are fetched separately and kept in their own
      // field — `roles` must stay org-scoped, because the Role select reads
      // roles[0] and the Remove button tests for 'org_owner'.
      const [profilesRes, rolesRes, platformRolesRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('org_id', org.id).neq('status', 'deactivated'),
        supabase.from('user_roles').select('user_id, role').eq('org_id', org.id),
        supabase.from('user_roles').select('user_id, role').is('org_id', null),
      ]);
      if (profilesRes.error) throw profilesRes.error;
      const rolesMap = new Map<string, string[]>();
      for (const r of rolesRes.data ?? []) { const existing = rolesMap.get(r.user_id) ?? []; existing.push(r.role); rolesMap.set(r.user_id, existing); }
      // RLS may return nothing here for a non-staff caller. That is fine: an
      // empty list only ever makes the client-book switch MORE hidden, never
      // less, because rendering it also requires the viewer to be staff.
      const platformMap = new Map<string, string[]>();
      for (const r of platformRolesRes.data ?? []) { const existing = platformMap.get(r.user_id) ?? []; existing.push(r.role); platformMap.set(r.user_id, existing); }
      return (profilesRes.data ?? []).map(p => ({
        ...p,
        roles: rolesMap.get(p.id) ?? [],
        platformRoles: platformMap.get(p.id) ?? [],
      }));
    },
    enabled: !!org,
  });

  const { data: invites = [] } = useQuery({
    queryKey: ['org-invites', org?.id],
    queryFn: async () => {
      if (!org) return [];
      const { data, error } = await supabase.from('org_invites').select('*').eq('org_id', org.id).order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!org,
  });

  // Fetch assignments and scenarios for member detail
  const { data: assignments = [] } = useQuery({
    queryKey: ['org-team-assignments', org?.id],
    queryFn: async () => {
      if (!org) return [];
      const { data, error } = await supabase.from('case_assignments').select('client_id, assigned_to').eq('org_id', org.id);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!org,
  });

  const { data: scenarios = [] } = useQuery({
    queryKey: ['org-team-scenarios', org?.id],
    queryFn: async () => {
      if (!org) return [];
      const { data, error } = await supabase.from('scenarios').select('id, name, client_id, user_id, is_draft').eq('org_id', org.id);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!org,
  });

  const createInviteMut = useMutation({
    mutationFn: async () => {
      if (!org) throw new Error('No org');
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email: inviteEmail || undefined,
          type: 'org_member',
          org_id: org.id,
          role: inviteRole,
          org_name: org.name,
        },
      });
      if (error) {
        let msg = error.message;
        try { const body = await (error as any).context?.json(); if (body?.error) msg = body.error; } catch { /* not JSON: keep the generic message */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (data: any) => {
      setGeneratedLink(`${window.location.origin}/invite/${data.token}`);
      queryClient.invalidateQueries({ queryKey: ['org-invites'] });
      logAudit('invite.created', 'invite', data.id, { email: inviteEmail || 'open', role: inviteRole });
      if (data.emailSent) {
        toast.success('Invite sent', { description: `Email sent to ${inviteEmail}` });
      } else if (inviteEmail) {
        toast.success('Invite created', { description: 'Could not send email. Share the link below.' });
      } else {
        toast.success('Invite created');
      }
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const removeUserMut = useMutation({
    mutationFn: async (userId: string) => {
      // .select() so a row-level-security refusal is visible. Postgres
      // reports a blocked DELETE as zero rows affected, not an error, so
      // without this the toast claims success for a write that never landed.
      const { data, error: roleErr } = await supabase
        .from('user_roles').delete().eq('user_id', userId).eq('org_id', org!.id).select('user_id');
      if (roleErr) throw roleErr;
      if (!data || data.length === 0) {
        throw new Error('permission denied: you cannot remove members of this organization');
      }
      return userId;
    },
    onSuccess: (userId) => {
      queryClient.invalidateQueries({ queryKey: ['org-members'] });
      logAudit('user.removed', 'user', userId);
      toast.success('User removed from org');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const deleteInviteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('org_invites').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['org-invites'] });
      logAudit('invite.revoked', 'invite', id);
      toast.success('Invite revoked');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  // Resend = restart the 7-day clock on the SAME token (the link already in
  // their inbox works again) and email it again. Expired invites are the main
  // customer: nobody accepted, so the advisor wants a second shot, not a
  // brand-new invite they have to revoke the old one for.
  const resendInviteMut = useMutation({
    mutationFn: async (inv: { id: string; token: string; email: string | null }) => {
      const { data, error } = await supabase.functions.invoke('send-invite-email', {
        body: { token: inv.token, resend: true },
      });
      if (error) {
        let msg = error.message;
        try {
          const body = await (error as { context?: { json?: () => Promise<{ error?: string }> } }).context?.json?.();
          if (body?.error) msg = body.error;
        } catch { /* body was not JSON — keep the transport message */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      return { inv, emailSent: !!data?.emailSent };
    },
    onSuccess: ({ inv, emailSent }) => {
      queryClient.invalidateQueries({ queryKey: ['org-invites'] });
      logAudit('invite.sent', 'invite', inv.id, { resend: true, email: inv.email ?? 'open' });
      if (emailSent) {
        toast.success('Invite resent', { description: `Email sent to ${inv.email}. The link is good for another ${INVITE_TTL_DAYS} days.` });
      } else {
        toast.success('Invite renewed', { description: `The link is good for another ${INVITE_TTL_DAYS} days. Copy it and send it yourself.` });
      }
    },
    onError: (err: unknown) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const changeRoleMut = useMutation({
    mutationFn: async ({ userId, newRole }: { userId: string; newRole: string }) => {
      const { error } = await supabase
        .from('user_roles')
        .update({ role: newRole as any })
        .eq('user_id', userId)
        .eq('org_id', org!.id);
      if (error) throw error;
      return { userId, newRole };
    },
    onSuccess: ({ userId, newRole }) => {
      queryClient.invalidateQueries({ queryKey: ['org-members'] });
      logAudit('role.changed', 'user', userId, { new_role: newRole });
      toast.success('Role updated');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  // Does this member carry a client book of their own? Platform staff only —
  // an org owner or admin must not get to decide, including for themselves.
  // The RPC writes its own 'user.client_book_changed' audit row, so there is
  // deliberately no logAudit call here.
  const clientBookMut = useMutation({
    mutationFn: async ({ userId, enabled }: { userId: string; enabled: boolean }) => {
      // Not in the generated Supabase types (that file is off-limits) — same
      // cast the other hand-written RPCs use.
      const { error } = await (supabase.rpc as unknown as (
        fn: 'set_member_client_book',
        args: { p_user: string; p_enabled: boolean },
      ) => Promise<{ error: Error | null }>)('set_member_client_book', {
        p_user: userId,
        p_enabled: enabled,
      });
      if (error) throw error;
      return enabled;
    },
    onSuccess: (enabled) => {
      queryClient.invalidateQueries({ queryKey: ['org-members'] });
      toast.success(enabled ? 'Client book turned on' : 'Client book turned off');
    },
    onError: (err: unknown) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const copyToClipboard = (text: string) => { navigator.clipboard.writeText(text); toast.success('Copied to clipboard'); };

  const { open: openInvites, accepted: acceptedInvites } = partitionInvites(invites);
  const memberName = (id: string | null) => {
    const m = id ? members.find(x => x.id === id) : undefined;
    return m?.full_name || m?.email || null;
  };
  const selectedMember = members.find(m => m.id === selectedMemberId);
  // Who may remove a member, mirroring the RLS DELETE policy on user_roles:
  // the org's owner (never themselves) and platform staff. An org_admin has
  // no delete policy, so a Remove button for them was always a no-op that
  // reported success.
  const canRemoveMember = (m: { id: string; roles: string[] }) =>
    !m.roles.includes('org_owner') && m.id !== user?.id
    && (isOrgOwner || isSuperAdmin || isPlatformAdmin);
  // Platform staff (Drew, Andrew, Dylan) always carry their own client book.
  // The switch is never rendered for them; the RPC refuses them too.
  const memberIsPlatformStaff = !!selectedMember
    && ((selectedMember as { platformRoles?: string[] }).platformRoles ?? [])
         .some(r => r === 'super_admin' || r === 'platform_admin');

  // Alts / FIA for one person. Offered to platform staff and the org's owner
  // (set_user_flag's gate) for everyone EXCEPT platform staff: Drew, Andrew
  // and Dylan always have both (get_strategy_entitlements returns true for
  // staff before reading any switch). Hidden for yourself too.
  const canSetStrategies = !!selectedMember && !memberIsPlatformStaff && selectedMember.id !== user?.id
    && (isSuperAdmin || isPlatformAdmin || isOrgOwner);
  const strategyFlags = useQuery({
    queryKey: ['member-strategy-flags', user?.id, selectedMemberId],
    enabled: canSetStrategies && !!selectedMemberId,
    queryFn: () => fetchMemberStrategyFlags(selectedMemberId as string),
  });
  const strategyMut = useMutation({
    mutationFn: async ({ userId, flag, value }: { userId: string; flag: FlagKey; value: boolean | null }) => {
      if (value === null) await clearNodeFlag(`user-${userId}`, flag);
      else await setNodeFlag(`user-${userId}`, flag, value);
      return { flag, value };
    },
    onSuccess: ({ flag, value }) => {
      queryClient.invalidateQueries({ queryKey: ['member-strategy-flags'] });
      const label = flag === 'alts' ? 'Alts' : 'FIA';
      toast.success(value === null ? `${label} now follows ${org?.name ?? 'the organization'}` : `${label} turned ${value ? 'on' : 'off'}`);
    },
    onError: (err: unknown) => toast.error('Error', { description: sanitizeError(err) }),
  });

  // Member detail data
  const memberAssignments = selectedMemberId ? assignments.filter(a => a.assigned_to === selectedMemberId) : [];
  const memberScenarios = selectedMemberId ? scenarios.filter(s => s.user_id === selectedMemberId && !s.is_draft) : [];
  const memberClientIds = new Set(memberScenarios.map(s => s.client_id));

  // Map client_id to name
  const clientNameMap = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const s of scenarios) { if (!m.has(s.client_id)) m.set(s.client_id, s.name); }
    return m;
  }, [scenarios]);

  if (!org) {
    return <Card><CardContent className="py-12 text-center text-muted-foreground"><p>No account found. Contact your administrator.</p></CardContent></Card>;
  }

  return (
    <div className="space-y-6">
      {/* Team Members */}
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">Team Members ({members.length})</CardTitle>
            <CardDescription>{org.name}</CardDescription>
          </div>
          <Button size="sm" onClick={() => { setInviteOpen(true); setGeneratedLink(''); setInviteEmail(''); setInviteRole('agent'); }}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Invite Member
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map(m => {
                  const isOwner = m.roles.includes('org_owner');
                  const isSelf = m.id === user?.id;
                  // Matches the RLS DELETE policy on user_roles exactly: org
                  // owners (not themselves) and platform staff, nobody else.
                  // It used to include org_admins against non-admin targets,
                  // but no such policy exists — the delete was refused and the
                  // app still said "User removed from org" (Drew, 2026-09-22).
                  const canRemove = canRemoveMember(m);
                  return (
                    <TableRow key={m.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedMemberId(m.id)}>
                      <TableCell className="font-medium">{m.full_name || m.email || 'Unknown'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{m.email}</TableCell>
                      <TableCell><div className="flex gap-1">{m.roles.map(r => <RoleBadge key={r} role={r} />)}</div></TableCell>
                      <TableCell><Badge variant={m.status === 'active' ? 'secondary' : 'destructive'} className="text-xs capitalize">{m.status}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{m.last_sign_in_at ? formatDistanceToNow(new Date(m.last_sign_in_at), { addSuffix: true }) : 'Never'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}</TableCell>
                      <TableCell>
                        <div onClick={e => e.stopPropagation()}>
                          {canRemove && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => removeUserMut.mutate(m.id)}>
                              <UserMinus className="h-3 w-3 mr-1" /> Remove
                            </Button>
                          )}
                          {isSelf && <span className="text-xs text-muted-foreground">You</span>}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Invites — pending (incl. expired, still resendable) vs accepted */}
      {invites.length > 0 && (
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Invites</CardTitle>
            <div className="flex gap-1" role="tablist" aria-label="Invite status">
              <Button size="sm" variant={inviteView === 'open' ? 'default' : 'ghost'} className="h-7 text-xs"
                role="tab" aria-selected={inviteView === 'open'} onClick={() => setInviteView('open')}>
                Pending ({openInvites.length})
              </Button>
              <Button size="sm" variant={inviteView === 'accepted' ? 'default' : 'ghost'} className="h-7 text-xs"
                role="tab" aria-selected={inviteView === 'accepted'} onClick={() => setInviteView('accepted')}>
                Accepted ({acceptedInvites.length})
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {inviteView === 'open' ? (
              openInvites.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No pending invites.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead className="w-28">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {openInvites.map(inv => {
                      const expired = inviteStatus(inv) === 'expired';
                      const busy = resendInviteMut.isPending && resendInviteMut.variables?.id === inv.id;
                      return (
                        <TableRow key={inv.id}>
                          <TableCell className="text-sm">{inv.email || <span className="text-muted-foreground italic">Open invite</span>}</TableCell>
                          <TableCell><RoleBadge role={inv.role} /></TableCell>
                          <TableCell className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(inv.created_at), { addSuffix: true })}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {expired
                              ? <Badge variant="destructive" className="text-xs">Expired</Badge>
                              : formatDistanceToNow(new Date(inv.expires_at), { addSuffix: true })}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {!expired && (
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Copy invite link" aria-label="Copy invite link"
                                  onClick={() => copyToClipboard(`${window.location.origin}/invite/${inv.token}`)}>
                                  <Copy className="h-3 w-3" />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title={inv.email ? 'Resend invite (restarts the timer)' : 'Renew invite (restarts the timer)'} aria-label="Resend invite"
                                disabled={busy} onClick={() => resendInviteMut.mutate({ id: inv.id, token: inv.token, email: inv.email })}>
                                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" title="Revoke invite" aria-label="Revoke invite"
                                onClick={() => deleteInviteMut.mutate(inv.id)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )
            ) : (
              acceptedInvites.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No accepted invites yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined as</TableHead>
                      <TableHead>Accepted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {acceptedInvites.map(inv => (
                      <TableRow key={inv.id}>
                        <TableCell className="text-sm">{inv.email || <span className="text-muted-foreground italic">Open invite</span>}</TableCell>
                        <TableCell><RoleBadge role={inv.role} /></TableCell>
                        <TableCell className="text-sm">{memberName(inv.redeemed_by) ?? <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{inv.redeemed_at ? formatDistanceToNow(new Date(inv.redeemed_at), { addSuffix: true }) : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            )}
          </CardContent>
        </Card>
      )}

      {/* Member Detail Sheet */}
      <Sheet open={!!selectedMember} onOpenChange={open => { if (!open) setSelectedMemberId(null); }}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          {selectedMember && (
            <>
              <SheetHeader>
                <SheetTitle>{selectedMember.full_name || selectedMember.email || 'Unknown'}</SheetTitle>
              </SheetHeader>
              <div className="space-y-6 mt-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><Label className="text-muted-foreground text-xs">Email</Label><p>{selectedMember.email}</p></div>
                  <div><Label className="text-muted-foreground text-xs">Status</Label><p><Badge variant={selectedMember.status === 'active' ? 'secondary' : 'destructive'} className="text-xs capitalize">{selectedMember.status}</Badge></p></div>
                  <div><Label className="text-muted-foreground text-xs">Joined</Label><p>{formatDistanceToNow(new Date(selectedMember.created_at), { addSuffix: true })}</p></div>
                  <div><Label className="text-muted-foreground text-xs">Last Login</Label><p>{selectedMember.last_sign_in_at ? formatDistanceToNow(new Date(selectedMember.last_sign_in_at), { addSuffix: true }) : 'Never'}</p></div>
                </div>

                {/* Role change */}
                {isOrgOwner && !selectedMember.roles.includes('org_owner') && selectedMember.id !== user?.id && (
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select
                      value={selectedMember.roles[0] || 'agent'}
                      onValueChange={newRole => changeRoleMut.mutate({ userId: selectedMember.id, newRole })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agent">Agent</SelectItem>
                        <SelectItem value="org_admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Client book — only platform staff may flip it, and it is
                    never offered FOR platform staff. Drew, 2026-09-21: a live
                    switch on Andrew's panel is one mis-click from hiding his
                    own book of 50 clients, and nobody on the platform team
                    builds work on someone else's behalf. Hidden for yourself
                    too, for the same reason `set_member_status` refuses a
                    self-change: no self-lockout. `set_member_client_book`
                    refuses a staff target server-side as well — this is the
                    affordance, not the control. */}
                {(isSuperAdmin || isPlatformAdmin) && !memberIsPlatformStaff && selectedMember.id !== user?.id && (
                  <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
                    <div className="space-y-1">
                      <Label htmlFor="client-book-toggle" className="text-sm">Has own clients</Label>
                      <p className="text-xs text-muted-foreground">
                        Turn this off for someone who builds clients for the advisors rather than
                        owning a book. They work from All Clients and pick whose client it is.
                      </p>
                    </div>
                    <Switch
                      id="client-book-toggle"
                      checked={(selectedMember as { has_client_book?: boolean }).has_client_book !== false}
                      disabled={clientBookMut.isPending}
                      onCheckedChange={enabled => clientBookMut.mutate({ userId: selectedMember.id, enabled })}
                    />
                  </div>
                )}

                {/* Strategies this person can add. Never for platform staff. */}
                {canSetStrategies && (
                  <div className="rounded-lg border p-3 space-y-3">
                    <Label className="text-sm">Strategies</Label>
                    {strategyFlags.isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : strategyFlags.error ? (
                      <p className="text-xs text-muted-foreground">Couldn't load strategy access. {sanitizeError(strategyFlags.error)}</p>
                    ) : strategyFlags.data && (['alts', 'fia'] as const).map(flag => {
                      const st = strategyFlags.data[flag];
                      const on = st.set ?? st.org;
                      const label = flag === 'alts' ? 'Alts' : 'FIA';
                      return (
                        <div key={flag} className="flex items-start justify-between gap-4">
                          <div className="space-y-0.5">
                            <p className="text-sm font-medium">{label}</p>
                            <p className="text-xs text-muted-foreground">
                              {st.set === null ? `Follows ${org.name}` : 'Set for this person'}
                              {st.set !== null && (
                                <> · <button type="button" className="font-semibold text-primary hover:underline"
                                  disabled={strategyMut.isPending}
                                  onClick={() => strategyMut.mutate({ userId: selectedMember.id, flag, value: null })}>
                                  Reset
                                </button></>
                              )}
                            </p>
                          </div>
                          <Switch
                            aria-label={`${label} for ${selectedMember.full_name || selectedMember.email}`}
                            checked={on}
                            disabled={strategyMut.isPending}
                            onCheckedChange={value => strategyMut.mutate({ userId: selectedMember.id, flag, value })}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><Label className="text-muted-foreground text-xs">Clients</Label><p>{memberClientIds.size}</p></div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-wrap">
                  {canRemoveMember(selectedMember) && (
                    <Button size="sm" variant="destructive" onClick={() => { removeUserMut.mutate(selectedMember.id); setSelectedMemberId(null); }}>
                      <UserMinus className="h-3.5 w-3.5 mr-1" /> Remove from Org
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Invite Team Member</DialogTitle></DialogHeader>
          {!generatedLink ? (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Email (optional)</Label>
                <Input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="agent@example.com" />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">Agent</SelectItem>
                    {isOrgOwner && <SelectItem value="org_admin">Admin</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <p className="text-sm text-muted-foreground">Send this link to your team member:</p>
              <div className="flex gap-2">
                <Input value={generatedLink} readOnly className="font-mono text-xs" />
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(generatedLink)}><Copy className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          )}
          <DialogFooter>
            {!generatedLink ? (
              <>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button onClick={() => createInviteMut.mutate()} disabled={createInviteMut.isPending}>
                  {createInviteMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  Generate Invite
                </Button>
              </>
            ) : (
              <Button onClick={() => setInviteOpen(false)}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
